/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Copyright 2026 Bytefinger Technology (HK) Co., Limited. SPDX-License-Identifier: Apache-2.0 */
import { useEffect, useRef, useState } from 'react';

import { I18n } from '@adp-arch/i18n';

interface TaskView {
  task_id: string;
  status: string;
  stage?: string;
  progress?: string;
  billing_status?: string;
  partial_text?: string;
  report?: {
    title: string;
    body: string;
    model: string;
    citations: Array<{ title: string; url: string }>;
    artifact?: { id: string; url: string };
  };
  error?: { message?: string };
}

const terminal = new Set([
  'completed',
  'failed',
  'cancelled',
  'canceled',
  'interrupted',
]);
const loginRequiredStatus = 401;
const notFoundStatus = 404;
const retryIntervalMs = 2000;

class ResearchTaskAccessError extends Error {
  constructor(readonly status: number) {
    super('Research task unavailable');
  }
}

async function readResearchTask(taskId: string): Promise<TaskView> {
  const response = await fetch(
    `/api/research/tasks/${encodeURIComponent(taskId)}`,
    { credentials: 'same-origin' },
  );
  if (
    response.status === loginRequiredStatus ||
    response.status === notFoundStatus
  ) {
    throw new ResearchTaskAccessError(response.status);
  }
  if (!response.ok) {
    throw new Error('Research task unavailable');
  }
  const task = (await response.json()) as TaskView;
  if (task.status === 'completed' && task.report?.artifact) {
    const reportResponse = await fetch(
      `/api/research/tasks/${encodeURIComponent(taskId)}/report`,
      { credentials: 'same-origin' },
    );
    if (
      reportResponse.status === loginRequiredStatus ||
      reportResponse.status === notFoundStatus
    ) {
      throw new ResearchTaskAccessError(reportResponse.status);
    }
    if (!reportResponse.ok) {
      throw new Error('Research report unavailable');
    }
    task.report = await reportResponse.json();
  }
  return task;
}

function accessErrorMessage(
  error: unknown,
  login: string,
  unavailable: string,
) {
  if (error instanceof ResearchTaskAccessError) {
    return error.status === loginRequiredStatus ? login : unavailable;
  }
  return unavailable;
}

function useResearchTask(taskId: string, label: (typeof words)['en']) {
  const [task, setTask] = useState<TaskView>({
    task_id: taskId,
    status: 'queued',
  });
  const [cancelBusy, setCancelBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [loadError, setLoadError] = useState('');
  const deletedRef = useRef(false);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (!live || deletedRef.current) {
        return;
      }
      try {
        const next = await readResearchTask(taskId);
        if (!live || deletedRef.current) {
          return;
        }
        setTask(next);
        setLoadError('');
        if (!terminal.has(next.status)) {
          timer = setTimeout(poll, retryIntervalMs);
        }
      } catch (error) {
        if (!live) {
          return;
        }
        setLoadError(accessErrorMessage(error, label.login, label.unavailable));
        if (!(error instanceof ResearchTaskAccessError)) {
          timer = setTimeout(poll, retryIntervalMs);
        }
      }
    };
    void poll();
    return () => {
      live = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [taskId, label.login, label.unavailable]);

  const requestCancel = async () => {
    setCancelBusy(true);
    try {
      const response = await fetch(
        `/api/research/tasks/${encodeURIComponent(taskId)}/cancel`,
        { method: 'POST', credentials: 'same-origin' },
      );
      if (!response.ok) {
        throw new ResearchTaskAccessError(response.status);
      }
      setTask((await response.json()) as TaskView);
      setLoadError('');
    } catch (error) {
      setLoadError(accessErrorMessage(error, label.login, label.unavailable));
    } finally {
      setCancelBusy(false);
    }
  };

  const requestDelete = async () => {
    setDeleteBusy(true);
    try {
      const response = await fetch(
        `/api/research/tasks/${encodeURIComponent(taskId)}`,
        { method: 'DELETE', credentials: 'same-origin' },
      );
      if (!response.ok) {
        throw new ResearchTaskAccessError(response.status);
      }
      deletedRef.current = true;
      setDeleted(true);
      setTask({ task_id: taskId, status: 'deleted' });
      setLoadError('');
    } catch (error) {
      setLoadError(accessErrorMessage(error, label.login, label.unavailable));
    } finally {
      setDeleteBusy(false);
    }
  };

  return {
    task,
    cancelBusy,
    deleteBusy,
    deleted,
    loadError,
    requestCancel,
    requestDelete,
  };
}

export const getResearchTaskIds = (
  units: Array<{
    llmOutput: { content: string; extra_info?: { tool_name?: string } };
    apiResponse?: { content: string };
  }>,
): string[] => [
  ...new Set(
    units.flatMap(unit => {
      if (!unit.apiResponse?.content) {
        return [];
      }
      try {
        if (unit.llmOutput.extra_info?.tool_name !== 'start_deep_research') {
          const call = JSON.parse(unit.llmOutput.content) as {
            name?: unknown;
            function?: { name?: unknown };
          };
          if (
            call.name !== 'start_deep_research' &&
            call.function?.name !== 'start_deep_research'
          ) {
            return [];
          }
        }
        const result = JSON.parse(unit.apiResponse.content) as {
          task_id?: unknown;
        };
        return typeof result.task_id === 'string' &&
          result.task_id.startsWith('research-')
          ? [result.task_id]
          : [];
      } catch {
        return [];
      }
    }),
  ),
];

const words = {
  en: {
    title: 'Deep Research',
    sources: 'Sources',
    billingPending:
      'The report is complete, but provider usage was unavailable. Credit settlement needs administrator reconciliation.',
    failed: 'Research failed',
    cancelled: 'Research cancelled',
    interrupted: 'Research interrupted',
    pending: 'Research',
    liveReport: 'Live report draft',
    cancel: 'Cancel research',
    delete: 'Delete research task',
    deleting: 'Deleting…',
    deleted: 'Research task deleted',
    login: 'Login required to view research',
    unavailable: 'Research task unavailable',
  },
  'zh-CN': {
    title: '深度研究',
    sources: '来源',
    billingPending: '报告已完成，但服务商未返回用量数据。Credit 结算需要管理员进行核对。',
    failed: '研究失败',
    cancelled: '研究已取消',
    interrupted: '研究已中断',
    pending: '研究',
    liveReport: '实时研究报告草稿',
    cancel: '取消研究',
    delete: '删除研究任务',
    deleting: '正在删除…',
    deleted: '研究任务已删除',
    login: '登录后查看研究',
    unavailable: '研究任务不可用',
  },
  'zh-TW': {
    title: '深度研究',
    sources: '來源',
    billingPending: '報告已完成，但服務商未返回用量資料。Credit 結算需要管理員進行核對。',
    failed: '研究失敗',
    cancelled: '研究已取消',
    interrupted: '研究已中斷',
    pending: '研究',
    liveReport: '即時研究報告草稿',
    cancel: '取消研究',
    delete: '刪除研究任務',
    deleting: '正在刪除…',
    deleted: '研究任務已刪除',
    login: '登入後查看研究',
    unavailable: '研究任務無法使用',
  },
};

type ResearchTaskLabels = (typeof words)['en'];

const displayStatus = (status: string) => {
  switch (status) {
    case 'queued':
    case 'waiting_authorization':
    case 'awaiting_plan':
    case 'approved_pending_admission':
    case 'in_progress':
    case 'cancelling':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
    case 'canceled':
      return 'Cancelled';
    case 'interrupted':
      return 'Interrupted';
    default:
      return 'Research';
  }
};

const ResearchTaskDetails: React.FC<{
  deleted: boolean;
  error: string;
  labels: ResearchTaskLabels;
  task: TaskView;
}> = ({ deleted, error, labels, task }) => {
  if (deleted) {
    return <p className="mt-2">{labels.deleted}</p>;
  }
  if (error) {
    return <p className="mt-2">{error}</p>;
  }
  if (task.status === 'completed' && task.report) {
    return (
      <>
        {task.billing_status === 'usage_unknown' ? (
          <p className="mt-2 text-sm text-amber-700" role="status">
            {labels.billingPending}
          </p>
        ) : null}
        <div className="mt-2 font-semibold">{task.report.title}</div>
        <div className="mt-2 whitespace-pre-wrap break-words">
          {task.report.body}
        </div>
        {task.report.citations?.length ? (
          <div className="mt-3">
            <strong>{labels.sources}</strong>
            <ol className="list-decimal pl-5">
              {task.report.citations
                .filter(citation => /^https?:\/\//i.test(citation.url))
                .map(citation => (
                  <li key={citation.url}>
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {citation.title || citation.url}
                    </a>
                  </li>
                ))}
            </ol>
          </div>
        ) : null}
      </>
    );
  }
  if (task.status === 'failed') {
    return <p className="mt-2">{task.error?.message || labels.failed}</p>;
  }
  if (task.status === 'cancelled' || task.status === 'canceled') {
    return <p className="mt-2">{labels.cancelled}</p>;
  }
  if (task.status === 'interrupted') {
    return <p className="mt-2">{labels.interrupted}</p>;
  }
  return (
    <>
      <p className="mt-2" aria-live="polite">
        {labels.pending} {task.progress || task.stage || task.status}…
      </p>
      {task.partial_text ? (
        <div className="mt-3 rounded-md bg-gray-50 p-3">
          <p className="mb-2 text-xs font-semibold text-gray-600">
            {labels.liveReport}
          </p>
          <div
            className="max-h-80 overflow-y-auto whitespace-pre-wrap break-words text-sm"
            aria-live="off"
          >
            {task.partial_text}
          </div>
        </div>
      ) : null}
    </>
  );
};

const ResearchTaskActions: React.FC<{
  cancelBusy: boolean;
  deleteBusy: boolean;
  deleted: boolean;
  error: string;
  labels: ResearchTaskLabels;
  onCancel: () => void;
  onDelete: () => void;
  task: TaskView;
}> = ({
  cancelBusy,
  deleteBusy,
  deleted,
  error,
  labels,
  onCancel,
  onDelete,
  task,
}) => (
  <>
    {!deleted && !error ? (
      <div className="flex flex-wrap gap-2">
        {!terminal.has(task.status) && task.status !== 'cancelling' ? <button
          type="button"
          disabled={cancelBusy}
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {labels.cancel}
        </button> : null}
        <button
          type="button"
          disabled={deleteBusy}
          onClick={onDelete}
          className="rounded-md px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          {deleteBusy ? labels.deleting : labels.delete}
        </button>
      </div>
    ) : null}
  </>
);

export const ResearchTaskCard: React.FC<{ taskId: string }> = ({ taskId }) => {
  const language = I18n.language || 'en';
  const label =
    language === 'zh-TW'
      ? words['zh-TW']
      : language.startsWith('zh')
        ? words['zh-CN']
        : words.en;
  const {
    task,
    cancelBusy,
    deleteBusy,
    deleted,
    loadError,
    requestCancel,
    requestDelete,
  } = useResearchTask(taskId, label);

  return (
    <section className="mt-3 w-full overflow-hidden rounded-xl border border-solid border-gray-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <strong className="text-sm font-semibold">{label.title}</strong>
        {!deleted && !loadError ? <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">{displayStatus(task.status)}</span> : null}
      </header>
      <div className="space-y-3 p-4">
        <ResearchTaskDetails
          deleted={deleted}
          error={loadError}
          labels={label}
          task={task}
        />
        <ResearchTaskActions
          cancelBusy={cancelBusy}
          deleteBusy={deleteBusy}
          deleted={deleted}
          error={loadError}
          labels={label}
          onCancel={() => void requestCancel()}
          onDelete={() => void requestDelete()}
          task={task}
        />
      </div>
    </section>
  );
};
